<template>
  <div class="cod">
    <div v-if="customers.length" class="cod-head">
      <div class="cod-msg" v-if="message">{{ message }}</div>
      <div class="cod-tools">
        <input type="text" class="cod-search" v-model="search"
               placeholder="Search ID, name, $, notation, specs, country, zip…" aria-label="Search these orders" />
        <label class="cod-shipchk" title="On = all orders. Uncheck to show only orders missing a ship-to country/zip.">
          <input type="checkbox" v-model="withShipTo" /><span>Include orders with country/zip</span>
        </label>
      </div>
    </div>

    <table v-if="sortedCustomers.length" class="cod-parent">
      <thead>
        <tr>
          <th class="cod-exp-h"></th>
          <th class="cod-sort" @click="sortBy('customerId')">Customer ID <span class="cod-arrow">{{ arrow('customerId') }}</span></th>
          <th class="cod-sort" @click="sortBy('customerName')">Customer Name <span class="cod-arrow">{{ arrow('customerName') }}</span></th>
          <th class="cod-sort cod-num" @click="sortBy('totalSales')">Total <span class="cod-arrow">{{ arrow('totalSales') }}</span></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="c in sortedCustomers">
          <!-- Customer row — a territory-colored left rail (segmented when the customer's orders span
               multiple territories); the text itself stays neutral (black / white in dark mode). -->
          <tr :key="'c-' + c.customerId" class="cod-custrow" :style="{ '--terr-rail': railFor(c) }" @click="toggleCustomer(c.customerId)">
            <td class="cod-exp">{{ isCustOpen(c.customerId) ? '−' : '+' }}</td>
            <td><b>{{ c.customerId }}</b><button v-if="reattributed(c)" type="button" class="cod-why" @click.stop="$emit('whyhere', { customerId: c.customerId, event: $event })" title="Re-attributed to a different rep than originally sold — see the rep history">💡</button></td>
            <td><b>{{ c.customerName }}</b></td>
            <td class="cod-num"><b>{{ fmtMoney(c.totalSales) }}</b></td>
          </tr>

          <!-- Customer detail: insights box + documents -->
          <tr v-if="isCustOpen(c.customerId)" :key="'cd-' + c.customerId" class="cod-custdetail">
            <td colspan="4">
              <div class="cod-insights">
                <div class="cod-insights-title">Customer Order Insights</div>
                <div class="cod-insights-id"><b>{{ c.customerId }}</b> &nbsp; {{ c.customerName }}</div>
                <ul class="cod-insights-list">
                  <li><b>Total Orders:</b>
                    Invoiced: <b class="cod-hl">{{ c.invoicedOrders }}</b>
                    &nbsp;|&nbsp;
                    Not Invoiced: <b class="cod-hl">{{ c.notInvoicedOrders }}</b>
                  </li>
                  <li>Count of Machine and/or Part Types: <b class="cod-hl">{{ fmtNum(c.partTypeCount) }}</b></li>
                  <li>Count of Part Types Sold Across ALL Orders: <b class="cod-hl">{{ fmtNum(c.qtyTotal) }}</b></li>
                  <li>Total Sales: <b class="cod-hl">{{ fmtMoney(c.totalSales) }}</b></li>
                </ul>
                <div v-if="c.customerNotation" class="cod-note cod-note-cust">
                  <button type="button" class="cod-note-lbl cod-note-toggle" @click.stop="toggleNote(c.customerId + '-cnote')" :aria-expanded="isNoteOpen(c.customerId + '-cnote') ? 'true' : 'false'">
                    <span class="cod-note-caret">{{ isNoteOpen(c.customerId + '-cnote') ? '▾' : '▸' }}</span>📝 Customer Notation
                  </button>
                  <div v-if="isNoteOpen(c.customerId + '-cnote')" class="cod-note-txt">{{ c.customerNotation }}</div>
                </div>
                <div v-if="c.customerSpecs" class="cod-note cod-spec cod-note-cust">
                  <button type="button" class="cod-note-lbl cod-note-toggle" @click.stop="toggleNote(c.customerId + '-cspec')" :aria-expanded="isNoteOpen(c.customerId + '-cspec') ? 'true' : 'false'">
                    <span class="cod-note-caret">{{ isNoteOpen(c.customerId + '-cspec') ? '▾' : '▸' }}</span>📋 Customer Specifications
                  </button>
                  <div v-if="isNoteOpen(c.customerId + '-cspec')" class="cod-note-txt">{{ c.customerSpecs }}</div>
                </div>
                <button v-if="c.customerNotation || c.customerSpecs" type="button" class="cod-persp-btn"
                        @click.stop="$emit('perspective', { customer: { customerId: c.customerId, customerName: c.customerName }, event: $event })"
                        title="AI sales perspective on this customer's notes &amp; specs">💡 AI sales perspective</button>
              </div>

              <table class="cod-docs">
                <thead>
                  <tr><th class="cod-exp-h"></th><th>Document ID</th><th>Date</th><th class="cod-num">Total</th><th class="cod-act-h"></th></tr>
                </thead>
                <tbody>
                  <template v-for="d in c.docs">
                    <tr :key="'d-' + c.customerId + '-' + d.docId" class="cod-docrow" :style="{ '--terr-rail': docRail(d) }" :title="d.territory ? ('Territory: ' + d.territory) : ''" @click="toggleDoc(c.customerId, d.docId)">
                      <td class="cod-exp cod-exp-doc">{{ isDocOpen(c.customerId, d.docId) ? '−' : '+' }}</td>
                      <td>{{ d.docId }}<span v-if="!d.isInvoice" class="cod-ordertag">order</span></td>
                      <td>{{ fmtLong(d.docDate) }}</td>
                      <td class="cod-num">{{ fmtMoney(d.total) }}</td>
                      <td class="cod-act">
                        <button type="button" class="cod-print"
                                :title="'Print ' + (d.isInvoice ? 'invoice' : 'order') + ' ' + d.docId"
                                :aria-label="'Print ' + (d.isInvoice ? 'invoice' : 'order') + ' ' + d.docId"
                                @click.stop="$emit('printdoc', { customer: c, doc: d })">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                               fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                               stroke-linejoin="round" aria-hidden="true">
                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                            <rect x="6" y="14" width="12" height="8"></rect>
                          </svg>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="isDocOpen(c.customerId, d.docId)" :key="'dp-' + c.customerId + '-' + d.docId" class="cod-partsrow">
                      <td colspan="5">
                        <div v-if="d.orderNotation" class="cod-note cod-note-order">
                          <button type="button" class="cod-note-lbl cod-note-toggle" @click.stop="toggleNote(c.customerId + '-' + d.docId + '-onote')" :aria-expanded="isNoteOpen(c.customerId + '-' + d.docId + '-onote') ? 'true' : 'false'">
                            <span class="cod-note-caret">{{ isNoteOpen(c.customerId + '-' + d.docId + '-onote') ? '▾' : '▸' }}</span>📝 Order Notation
                          </button>
                          <div v-if="isNoteOpen(c.customerId + '-' + d.docId + '-onote')" class="cod-note-txt">{{ d.orderNotation }}</div>
                        </div>
                        <div v-if="d.orderSpecs || d.shipTo" class="cod-order-meta">
                          <div v-if="d.orderSpecs" class="cod-note cod-spec cod-note-order cod-ospec">
                            <button type="button" class="cod-note-lbl cod-note-toggle" @click.stop="toggleNote(c.customerId + '-' + d.docId + '-ospec')" :aria-expanded="isNoteOpen(c.customerId + '-' + d.docId + '-ospec') ? 'true' : 'false'">
                              <span class="cod-note-caret">{{ isNoteOpen(c.customerId + '-' + d.docId + '-ospec') ? '▾' : '▸' }}</span>📋 Order Specifications
                            </button>
                            <div v-if="isNoteOpen(c.customerId + '-' + d.docId + '-ospec')" class="cod-note-txt">{{ d.orderSpecs }}</div>
                          </div>
                          <div v-if="d.shipTo" class="cod-shipto" title="Ship-to address for this order"><span class="cod-shipto-ic" aria-hidden="true">📦</span>{{ d.shipTo }}</div>
                        </div>
                        <table class="cod-parts">
                          <thead>
                            <tr>
                              <th>Part ID</th><th>Part Description</th>
                              <th class="cod-num">Unit Price</th><th class="cod-num">Qty</th>
                              <th class="cod-num">Line Amount</th><th>Part Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(p, pi) in d.parts" :key="'pt-' + pi">
                              <td class="cod-partid">{{ p.partNumber }}</td>
                              <td>{{ p.partDescription }}<div v-if="p.partNotation" class="cod-note-part">📝 {{ p.partNotation }}</div><div v-if="p.partSpecs" class="cod-note-part cod-spec-part">📋 {{ p.partSpecs }}</div></td>
                              <td class="cod-num">{{ fmtMoney(p.unitPrice) }}</td>
                              <td class="cod-num">{{ fmtNum(p.qty) }}</td>
                              <td class="cod-num">{{ fmtMoney(p.lineTotal) }}</td>
                              <td>{{ p.partType }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <div v-else-if="customers.length" class="cod-empty">No customers match your search{{ withShipTo ? '' : ' — none are missing a ship-to country/zip' }}.</div>
    <div v-else class="cod-empty">Please narrow your selection. Orders will appear here.</div>
  </div>
</template>

<script>
export default {
  name: "CustomerOrderDetails",
  props: {
    customers: { type: Array, default: () => [] },
    message: { type: String, default: "" },
    // (territoryName) => css color — for the territory-colored left rail on each customer row.
    colorFor: { type: Function, default: null },
    // customerId -> the customer's territory color (fallback rail color before the per-order territory
    // breakdown is available from the backend).
    customerColorMap: { type: Object, default: null },
  },
  data() {
    return { openC: {}, openD: {}, openNote: {}, sortKey: "totalSales", sortDir: "desc", search: "", withShipTo: true };
  },
  computed: {
    // Search (ID / name / $ / notation / specs / country / zip) + the "include orders with country/zip" checkbox.
    // Frontend-only over the already-loaded data. When the checkbox is UNCHECKED, keep only orders MISSING a ship-to
    // (country/zip) — dropping customers that have none — so users can audit the gaps.
    filteredCustomers() {
      const q = (this.search || "").trim().toLowerCase();
      const withShip = this.withShipTo;
      const hasShip = (d) => !!(d && d.shipTo && String(d.shipTo).trim());
      const out = [];
      (this.customers || []).forEach((c) => {
        let docs = c.docs || [];
        if (!withShip) {
          const missing = docs.filter((d) => !hasShip(d));
          if (!missing.length) return;                    // no gaps for this customer → hide it
          c = Object.assign({}, c, { docs: missing });     // shallow copy with only the missing-ship-to orders
          docs = missing;
        }
        if (q) {
          const custHit = [c.customerId, c.customerName, c.customerNotation, c.customerSpecs,
                           String(c.totalSales == null ? "" : c.totalSales), this.fmtMoney(c.totalSales)]
                          .some((v) => v && String(v).toLowerCase().indexOf(q) >= 0);
          const docHit = docs.some((d) => {
            if (!d) return false;
            const hay = (d.shipTo || "") + " " + (d.docId || "") + " " +
                        (d.total == null ? "" : d.total + " " + this.fmtMoney(d.total));
            return hay.toLowerCase().indexOf(q) >= 0;
          });
          if (!custHit && !docHit) return;
        }
        out.push(c);
      });
      return out;
    },
    sortedCustomers() {
      const k = this.sortKey;
      const dir = this.sortDir === "asc" ? 1 : -1;
      return (this.filteredCustomers || []).slice().sort((a, b) => {
        if (k === "totalSales") return ((Number(a.totalSales) || 0) - (Number(b.totalSales) || 0)) * dir;
        const av = (a[k] || "").toString().toLowerCase();
        const bv = (b[k] || "").toString().toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    },
  },
  watch: {
    // collapse everything when the underlying data changes (new filters)
    customers() { this.openC = {}; this.openD = {}; },
  },
  methods: {
    // The CSS background for a customer row's left rail: a solid territory color, OR a vertical segmented
    // gradient (proportional to each territory's sales) when the customer's orders span >1 territory.
    railFor(c) {
      var self = this;
      var colorOf = function (name) { return (self.colorFor && self.colorFor(name)) || "#9e2436"; };
      var segs = ((c && c.territories) || []).filter(function (t) { return (Number(t.sales) || 0) > 0; });
      if (segs.length > 1) {
        var total = segs.reduce(function (s, t) { return s + (Number(t.sales) || 0); }, 0) || 1;
        var acc = 0; var stops = [];
        segs.forEach(function (t) {
          var from = acc / total * 100; acc += (Number(t.sales) || 0); var to = acc / total * 100;
          stops.push(colorOf(t.name) + " " + from.toFixed(1) + "% " + to.toFixed(1) + "%");
        });
        return "linear-gradient(to bottom, " + stops.join(", ") + ")";
      }
      if (segs.length === 1) return colorOf(segs[0].name);
      // No per-order territory data yet → fall back to the customer's single territory color from the parent.
      var m = this.customerColorMap || {};
      return (c && m[c.customerId]) || "transparent";
    },
    // Each ORDER row gets a solid territory-colored left rail (an order belongs to one rep/territory), so a rep can
    // see at a glance which orders belong to which territory — mirroring the parent customer row's rail.
    docRail(d) {
      var name = d && d.territory ? String(d.territory) : "";
      if (!name || name === "(Unassigned)") return "transparent";
      return (this.colorFor && this.colorFor(name)) || "transparent";
    },
    // A customer was RE-ATTRIBUTED if its VISUAL rep history (repHistory) includes a rep different from its current
    // (mapped) rep — i.e. some of its orders were originally sold by someone else. Drives the 💡 rep-history icon.
    reattributed(c) {
      if (!c || !Array.isArray(c.repHistory) || !c.repHistory.length) return false;
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      var cur = norm(c.currentRep);
      return c.repHistory.some(function (h) { return h && norm(h.rep) !== cur; });
    },
    toggleCustomer(id) { this.$set(this.openC, id, !this.openC[id]); },
    isCustOpen(id) { return !!this.openC[id]; },
    toggleDoc(cid, did) { const k = cid + "|" + did; this.$set(this.openD, k, !this.openD[k]); },
    isDocOpen(cid, did) { return !!this.openD[cid + "|" + did]; },
    // Notation / Specifications blocks are collapsed by default — click the header to expand.
    toggleNote(k) { this.$set(this.openNote, k, !this.openNote[k]); },
    isNoteOpen(k) { return !!this.openNote[k]; },
    sortBy(k) {
      if (this.sortKey === k) this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      else { this.sortKey = k; this.sortDir = (k === "totalSales" ? "desc" : "asc"); }   // money → biggest-first on first click
    },
    arrow(k) { return this.sortKey === k ? (this.sortDir === "asc" ? "▲" : "▼") : "▲▼"; },
    fmtMoney(n) { return "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    fmtNum(n) { return (Number(n) || 0).toLocaleString("en-US"); },
    fmtLong(d) {
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d).slice(0, 10);
      return dt.toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
    },
  },
};
</script>

<style scoped>
.cod { font-size: 11.5px; }
.cod-msg { background: #fff; padding: 8px 4px 12px; color: #222; font-weight: 700; font-size: 12px; }
/* Header toolbar: intro message (left) + search box & missing-ship-to checkbox (right). */
.cod-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 6px 16px; flex-wrap: wrap; }
.cod-head .cod-msg { flex: 1 1 300px; }
.cod-tools { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 0 4px 8px; }
.cod-search { border: 1px solid #c9ccd2; border-radius: 6px; padding: 6px 10px; font-size: 12px; width: 300px; max-width: 100%; }
.cod-search:focus { outline: none; border-color: #7a1f2b; box-shadow: 0 0 0 2px rgba(122,31,43,.15); }
.cod-shipchk { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; color: #555; white-space: nowrap; cursor: pointer; }
.cod-shipchk input { cursor: pointer; margin: 0; }
html.dark-mode .cod-search { background: #1f242b; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .cod-shipchk { color: #aeb6bf; }

.cod-parent { width: 100%; border-collapse: collapse; border: 2px solid #222; }
.cod-parent > thead th { background: #f2f2f2; border: 1px solid #999; padding: 6px 8px; text-align: left; cursor: pointer; user-select: none; font-size: 11px; }
.cod-parent > thead th.cod-num { text-align: right; }
.cod-arrow { font-size: 9px; color: #888; margin-left: 3px; }
.cod-num { text-align: right; }
.cod-exp-h { width: 28px; cursor: default !important; }

/* Customer rows: black text (white in dark mode); the territory color rides a left rail (the --terr-rail
   ::before on the first cell), segmented when the customer's orders span multiple territories. */
.cod-custrow { background: transparent; color: #111; cursor: pointer; }
.cod-custrow > td { padding: 6px 8px; border: 1px solid rgba(127,127,127,.22); }
.cod-custrow > td:first-child { position: relative; }
.cod-custrow > td:first-child::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 5px; background: var(--terr-rail, transparent); }
.cod-custrow:hover { background: rgba(127,127,127,.10); }
/* Same territory-colored left rail on each ORDER row (to the left of the + expand), so a rep can tell which
   orders belong to which territory. z-index lifts it above the gray expand-cell background. */
.cod-docrow > td:first-child { position: relative; }
.cod-docrow > td:first-child::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 5px; background: var(--terr-rail, transparent); z-index: 1; }
.cod-exp { width: 28px; text-align: center; background: transparent; color: #555; font-weight: 700; cursor: pointer; }
.cod-why { border: 0; background: none; cursor: pointer; font-size: 12px; line-height: 1; padding: 0 0 0 6px; opacity: .85; }
.cod-why:hover { opacity: 1; transform: scale(1.12); }
.cod-exp-doc { background: #888; }

.cod-custdetail > td { padding: 8px 10px; background: #fafafa; border: 1px solid #ccc; }

/* Insights box */
.cod-insights { border: 1px solid #777; background: #fff; padding: 8px 10px; margin: 2px 0 10px; }
.cod-insights-title { font-weight: 800; color: var(--ci-accent, #BB2E38); margin-bottom: 4px; }
.cod-insights-id { margin-bottom: 4px; }
.cod-insights-list { margin: 6px 0 0; padding-left: 18px; }
.cod-insights-list li { margin: 2px 0; }
.cod-hl { color: var(--ci-accent, #BB2E38); }

/* Documents + parts tables */
.cod-docs, .cod-parts { width: 100%; border-collapse: collapse; background: #fff; }
.cod-docs th, .cod-docs td, .cod-parts th, .cod-parts td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; }
.cod-docs th, .cod-parts th { background: #f2f2f2; font-weight: 700; }
.cod-docs th.cod-num, .cod-docs td.cod-num, .cod-parts th.cod-num, .cod-parts td.cod-num { text-align: right; }
.cod-docrow { cursor: pointer; }
.cod-docrow:hover { background: #f6f6f6; }
.cod-partsrow > td { background: #f7f7f7; padding: 4px 6px 6px; }
.cod-parts { font-size: 11px; }
.cod-partid { font-weight: 600; }
.cod-ordertag { font-size: 9px; background: #fff4e0; color: #8a6d1a; border: 1px solid #f0c36d; border-radius: 8px; padding: 0 5px; margin-left: 6px; }
/* Per-order 🖨 → the parent prints THAT document on the Order-Details letterhead. */
.cod-act-h, .cod-act { width: 30px; text-align: center; }
.cod-print { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 20px; padding: 0; border: 1px solid #d7dce1; border-radius: 4px; background: #fff; color: #7a1f2b; cursor: pointer; }
.cod-print:hover { border-color: #7a1f2b; background: #fbeff1; }
html.dark-mode .cod-print { background: #1f242b; border-color: #3a414a; color: #f0a8b1; }
html.dark-mode .cod-print:hover { background: #3a2226; border-color: #f0a8b1; }
/* VMFG notations (customer / order / part) surfaced in the drill-down. */
.cod-note { border: 1px solid #f0d69a; border-left: 3px solid #d9a441; background: #fdf6e3; border-radius: 4px; padding: 5px 8px; margin: 0 0 8px; }
.cod-note-lbl { font-weight: 800; color: #8a6d1a; font-size: 10.5px; }
.cod-note-toggle { display: flex; align-items: center; gap: 5px; width: 100%; border: 0; background: none; cursor: pointer; text-align: left; padding: 0; font: inherit; }
.cod-note-caret { font-size: 9px; opacity: .7; }
.cod-note-txt { white-space: pre-wrap; word-break: break-word; color: #5a4a1e; margin-top: 2px; }
.cod-note-part { margin-top: 3px; font-size: 10px; color: #8a6d1a; white-space: pre-wrap; word-break: break-word; }
html.dark-mode .cod-note { background: #2c2617; border-color: #5a4a1e; border-left-color: #d9a441; }
html.dark-mode .cod-note-lbl { color: #e6c65f; }
html.dark-mode .cod-note-txt { color: #cbbfa4; }
html.dark-mode .cod-note-part { color: #e6c65f; }
/* Specifications (📋) — blue variant of the note block, and the customer-level 💡 perspective button. */
.cod-spec { border-color: #bcd6ef; border-left-color: #2f6fb0; background: #eef5fc; }
.cod-spec .cod-note-lbl { color: #245a91; }
.cod-spec .cod-note-txt { color: #274a63; }
.cod-spec-part { color: #245a91; }
/* Per-order meta strip: Order Specifications (left) + ship-to address (right, right-aligned). */
.cod-order-meta { display: flex; align-items: flex-start; gap: 12px; margin: 0 0 8px; flex-wrap: wrap; }
.cod-order-meta .cod-ospec { flex: 0 1 auto; min-width: 0; margin: 0; }
.cod-shipto { flex: 0 0 auto; margin-left: auto; max-width: 48%; text-align: right; font-size: 11.5px; color: #555; line-height: 1.35; padding-top: 3px; overflow-wrap: anywhere; }
.cod-shipto-ic { margin-right: 4px; opacity: .8; }
html.dark-mode .cod-shipto { color: #aeb6bf; }
.cod-persp-btn { border: 1px solid #e0c48a; background: #fff8e8; color: #8a6d1a; font-weight: 700; font-size: 10.5px; border-radius: 6px; padding: 3px 8px; cursor: pointer; margin-top: 2px; }
.cod-persp-btn:hover { background: #fdefc8; }
html.dark-mode .cod-spec { background: #16222c; border-color: #2f4a63; border-left-color: #4a90d9; }
html.dark-mode .cod-spec .cod-note-lbl { color: #8fc0ef; }
html.dark-mode .cod-spec .cod-note-txt { color: #b9d4ea; }
html.dark-mode .cod-spec-part { color: #8fc0ef; }
html.dark-mode .cod-persp-btn { background: #2c2617; border-color: #5a4a1e; color: #e6c65f; }

.cod-empty { padding: 16px; color: #999; font-size: 12px; background: #fff; }

/* Mobile (< 992px): shrink the type + WRAP long customer names so the parent table fits a phone (it was running off
   the right edge). table-layout:fixed pins the ID/Total columns so Total stays on-screen + the Name column wraps.
   The deep doc/parts sub-tables are inherently wide → let them scroll horizontally inside the card. */
@media (max-width: 991.98px) {
  .cod { font-size: 10px; }
  /* All 3 nested levels use fixed layout so columns can't push the table past the card width. */
  .cod-parent, .cod-docs, .cod-parts { table-layout: fixed; width: 100%; }
  .cod-num { white-space: nowrap; }
  /* Parent (Customer ID / Name / Total) — pin ID + Total, Name wraps. */
  .cod-parent > thead th { font-size: 9.5px; padding: 4px 5px; }
  .cod-custrow > td { padding: 4px 5px; word-break: break-word; overflow-wrap: anywhere; }
  .cod-parent th:nth-child(1), .cod-parent td:nth-child(1) { width: 20px; }
  .cod-parent th:nth-child(2), .cod-parent td:nth-child(2) { width: 58px; }
  .cod-parent th:nth-child(4), .cod-parent td:nth-child(4) { width: 82px; }
  .cod-custdetail > td { padding: 6px 4px; }
  /* Documents (4 col) — pin expand + Total; Document ID / Date share the rest + wrap. */
  .cod-docs { font-size: 9.5px; }
  .cod-docs th, .cod-docs td { padding: 3px 4px; word-break: break-word; overflow-wrap: anywhere; }
  .cod-docs th:nth-child(1), .cod-docs td:nth-child(1) { width: 18px; }
  .cod-docs th:nth-child(4), .cod-docs td:nth-child(4) { width: 66px; }
  /* Parts (6 col) — pin the narrow numerics; Description + Part Type take the remainder + wrap → FITS, no cutoff. */
  .cod-parts { font-size: 9px; }
  .cod-parts th, .cod-parts td { padding: 3px 3px; word-break: break-word; overflow-wrap: anywhere; }
  .cod-parts th:nth-child(1), .cod-parts td:nth-child(1) { width: 44px; }
  .cod-parts th:nth-child(3), .cod-parts td:nth-child(3) { width: 46px; }
  .cod-parts th:nth-child(4), .cod-parts td:nth-child(4) { width: 28px; }
  .cod-parts th:nth-child(5), .cod-parts td:nth-child(5) { width: 50px; }
  /* Safety net if a value still can't wrap: scroll the parts block rather than break the page. */
  .cod-partsrow > td { overflow-x: auto; }
}
</style>
